import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getReferralContent, addReferralContent, updateReferralContent, clearReferralContent, uploadReferralContentAsset, uploadBenefitIcon } from '../../actions/referralContent';
import { 
    FileText, 
    Save, 
    ArrowLeft, 
    Plus, 
    Trash2, 
    Image as ImageIcon, 
    Video, 
    Info,
    HelpCircle,
    Tag,
    Type,
    Edit3,
    Upload,
    Eye,
    MoveUp,
    MoveDown,
    AlertCircle,
    CheckCircle,
    Star
} from 'lucide-react';

const ReferralContent = ({ 
    getReferralContent, 
    addReferralContent, 
    updateReferralContent, 
    clearReferralContent, 
    uploadReferralContentAsset,
    uploadBenefitIcon,
    referralContent: { referralContent, loading, assetPath }
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [formData, setFormData] = useState({
        name: '',
        id: '',
        active: false,
        assets: [],
        benefits: [],
        faqs: [],
        message: ""
    });

    const [newBenefit, setNewBenefit] = useState({
        title: '',
        description: '',
        icon: null
    });

    // Benefit icon upload states
    const [benefitIconFile, setBenefitIconFile] = useState(null);
    const [uploadingBenefitIcon, setUploadingBenefitIcon] = useState(false);
    
    // Benefit editing states
    const [editingBenefitIndex, setEditingBenefitIndex] = useState(null);
    const [editingBenefit, setEditingBenefit] = useState({
        title: '',
        description: '',
        icon: null
    });
    const [editingBenefitIconFile, setEditingBenefitIconFile] = useState(null);
    


    const [newFaq, setNewFaq] = useState({
        title: '',
        description: ''
    });

    // File upload states
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadingAsset, setUploadingAsset] = useState(false);
    const [fileType, setFileType] = useState('image');
    const [previewAsset, setPreviewAsset] = useState(null);

    useEffect(() => {
        document.title = 'Referral Content';
        if (!isNew) {
            getReferralContent(id);
        } else {
            clearReferralContent();
        }
        
        return () => {
            clearReferralContent();
        };
    }, [id, isNew, getReferralContent, clearReferralContent]);

    useEffect(() => {
        if (referralContent && !isNew) {
            setFormData({
                name: referralContent.name || '',
                id: referralContent.id || '',
                active: referralContent.active || false,
                assets: referralContent.assets || [],
                benefits: referralContent.benefits || [],
                faqs: referralContent.faqs || [],
                message: referralContent.message || ''
            });
        }
    }, [referralContent, isNew]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            alert('Referral content name is required');
            return;
        }

        if (!formData.message.trim()) {
            alert('Referral content message is required');
            return;
        }

        if (isNew) {
            // For new items, save the referral content
            const result = await addReferralContent(formData);
            if (result) {
                navigate('/inventory/v1.2/referral-contents');
            } else {
                // If creation failed, don't navigate away
                console.error('Failed to create referral content');
            }
        } else {
            updateReferralContent(id, formData);
        }
    };



    // Benefits Management
    const addBenefit = async () => {
        if (newBenefit.title.trim() && newBenefit.description.trim()) {
            setFormData(prev => ({
                ...prev,
                benefits: [...prev.benefits, { 
                    ...newBenefit, 
                    icon: '' // No icon for new benefits
                }]
            }));
            
            setNewBenefit({
                title: '',
                description: '',
                icon: null
            });
        }
    };

    const removeBenefit = (index) => {
        setFormData(prev => ({
            ...prev,
            benefits: prev.benefits.filter((_, i) => i !== index)
        }));
    };

    const updateBenefit = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            benefits: prev.benefits.map((benefit, i) => 
                i === index ? { ...benefit, [field]: value } : benefit
            )
        }));
    };

    const startEditingBenefit = (index) => {
        const benefit = formData.benefits[index];
        setEditingBenefitIndex(index);
        setEditingBenefit({
            title: benefit.title || '',
            description: benefit.description || '',
            icon: benefit.icon || null
        });
        setEditingBenefitIconFile(null);
    };

    const cancelEditingBenefit = () => {
        setEditingBenefitIndex(null);
        setEditingBenefit({
            title: '',
            description: '',
            icon: null
        });
        setEditingBenefitIconFile(null);
    };

    const saveEditingBenefit = async () => {
        if (!editingBenefit.title.trim() || !editingBenefit.description.trim()) {
            alert('Title and description are required');
            return;
        }

        let iconPath = editingBenefit.icon;
        
        // Upload new icon if file is selected
        if (editingBenefitIconFile && !isNew) {
            setUploadingBenefitIcon(true);
            try {
                // Get the current benefit to find its ID or index
                const currentBenefit = formData.benefits[editingBenefitIndex];
                // Use the index as benefitId if no actual ID exists
                const benefitId = currentBenefit._id || editingBenefitIndex;
                
                const result = await uploadBenefitIcon(id, benefitId, editingBenefitIconFile);
                if (result) {
                    iconPath = result.path || editingBenefitIconFile.name;
                }
            } catch (error) {
                console.error('Icon upload error:', error);
                alert('Failed to upload icon. Please try again.');
                setUploadingBenefitIcon(false);
                return;
            } finally {
                setUploadingBenefitIcon(false);
            }
        }

        setFormData(prev => ({
            ...prev,
            benefits: prev.benefits.map((benefit, i) => 
                i === editingBenefitIndex ? { 
                    ...benefit, 
                    title: editingBenefit.title,
                    description: editingBenefit.description,
                    icon: iconPath
                } : benefit
            )
        }));

        // Reset editing states
        setEditingBenefitIndex(null);
        setEditingBenefit({
            title: '',
            description: '',
            icon: null
        });
        setEditingBenefitIconFile(null);

        // Reset file input
        const fileInput = document.getElementById(`editing-benefit-icon-${editingBenefitIndex}`);
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleBenefitIconChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                alert('Please select a valid image file for the icon');
                return;
            }
            
            // Validate file size (max 5MB for icons)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                alert('Icon file size must be less than 5MB');
                return;
            }
            
            setBenefitIconFile(file);
        }
    };

    const handleEditingBenefitIconChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                alert('Please select a valid image file for the icon');
                return;
            }
            
            // Validate file size (max 5MB for icons)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                alert('Icon file size must be less than 5MB');
                return;
            }
            
            setEditingBenefitIconFile(file);
        }
    };

    // FAQ Management
    const addFaq = () => {
        if (newFaq.title.trim() && newFaq.description.trim()) {
            setFormData(prev => ({
                ...prev,
                faqs: [...prev.faqs, { ...newFaq }]
            }));
            setNewFaq({
                title: '',
                description: ''
            });
        }
    };

    const removeFaq = (index) => {
        setFormData(prev => ({
            ...prev,
            faqs: prev.faqs.filter((_, i) => i !== index)
        }));
    };

    // File upload functions
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setUploadFile(file);
            
            // Auto-detect file type
            if (file.type.startsWith('image/')) {
                setFileType('image');
            } else if (file.type.startsWith('video/')) {
                setFileType('video');
            }
        }
    };

    const handleFileTypeChange = (e) => {
        setFileType(e.target.value);
    };

    const uploadAsset = async () => {
        if (!uploadFile || isNew) return;
        
        // File validation
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (uploadFile.size > maxSize) {
            alert('File size must be less than 50MB');
            return;
        }
        
        // Validate file type
        const isValidImage = uploadFile.type.startsWith('image/') && fileType === 'image';
        const isValidVideo = uploadFile.type.startsWith('video/') && fileType === 'video';
        
        if (!isValidImage && !isValidVideo) {
            alert(`Please select a valid ${fileType} file`);
            return;
        }
        
        setUploadingAsset(true);
        try {
            const result = await uploadReferralContentAsset(id, uploadFile);
            
            if (result) {
                // Clear the file input after successful upload
                setUploadFile(null);
                // Reset file input element
                const fileInput = document.querySelector(`input[type="file"][key="upload-${fileType}"]`);
                if (fileInput) {
                    fileInput.value = '';
                }
                // The action will refresh the referral content data automatically
            }
        } catch (error) {
            console.error('Upload error in component:', error);
        } finally {
            setUploadingAsset(false);
        }
    };

    const moveAsset = (index, direction) => {
        if (
            (direction === 'up' && index === 0) || 
            (direction === 'down' && index === formData.assets.length - 1)
        ) {
            return;
        }

        const updatedAssets = [...formData.assets];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap array positions
        [updatedAssets[index], updatedAssets[swapIndex]] = [updatedAssets[swapIndex], updatedAssets[index]];
        
        // Update priorities
        const reorderedAssets = updatedAssets.map((asset, i) => ({
            ...asset,
            priority: i + 1
        }));
        
        setFormData(prev => ({ ...prev, assets: reorderedAssets }));
    };

    const removeAssetFromList = (index) => {
        const updatedAssets = formData.assets.filter((_, i) => i !== index);
        // Recalculate priorities
        const reorderedAssets = updatedAssets.map((asset, i) => ({
            ...asset,
            priority: i + 1
        }));
        setFormData(prev => ({ ...prev, assets: reorderedAssets }));
    };

    const previewAssetFile = (asset) => {
        setPreviewAsset(asset);
    };

    const closePreview = () => {
        setPreviewAsset(null);
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading referral content...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <Link 
                            to="/inventory/v1.2/referral-contents" 
                            className="btn btn-outline-primary d-flex align-items-center mb-2"
                        >
                            <ArrowLeft size={16} className="me-2" />
                            Back to Referral Contents
                        </Link>
                        <h1 className="h3 mb-0 text-gray-800">
                            {isNew ? 'Create New Referral Content' : `Edit ${formData.name || 'Referral Content'}`}
                        </h1>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row">
                        {/* Main Content Form */}
                        <div className="col-lg-8">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Edit3 size={20} className="me-2 text-primary" />
                                        Referral Content Details
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">
                                                Referral Content Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Enter referral content name"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Content ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="id"
                                                value={formData.id}
                                                onChange={handleInputChange}
                                                placeholder="Unique identifier (optional)"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Message <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            placeholder="Use my code [CODE] and get [CANS] free cans on your first order!"
                                            rows="10"
                                            required
                                        />
                                        <small className="form-text text-muted">
                                            This is the main message that will be displayed to users. Include all important information about the referral program.
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="active"
                                                id="active"
                                                checked={formData.active}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label fw-bold" htmlFor="active">
                                                Active
                                            </label>
                                            <small className="form-text text-muted d-block">
                                                Enable this referral content to be visible and accessible
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Assets Section */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <ImageIcon size={20} className="me-2 text-success" />
                                        Assets ({formData.assets.length})
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {/* Assets List */}
                                    {formData.assets.length === 0 ? (
                                        <div className="alert alert-info d-flex align-items-center">
                                            <AlertCircle size={24} className="me-3" />
                                            <div>
                                                No assets added yet. {!isNew && "Use the upload section below to add assets."}
                                                {isNew && "Save the referral content first, then you can upload assets."}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {formData.assets.map((asset, index) => (
                                                <div key={index} className="col-md-6 col-lg-4">
                                                    <div className="card border h-100">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                <div className="d-flex align-items-center">
                                                                    {asset.assetType === 'image' ? (
                                                                        <ImageIcon size={20} className="me-2 text-primary" />
                                                                    ) : (
                                                                        <Video size={20} className="me-2 text-danger" />
                                                                    )}
                                                                    <span className="fw-bold text-muted">#{asset.priority}</span>
                                                                </div>
                                                                <div className="btn-group btn-group-sm">
                                                                    {asset.asset && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-info btn-sm"
                                                                            onClick={() => previewAssetFile(asset)}
                                                                            title="Preview"
                                                                        >
                                                                            <Eye size={14} />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => moveAsset(index, 'up')}
                                                                        disabled={index === 0}
                                                                        title="Move Up"
                                                                    >
                                                                        <MoveUp size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-secondary btn-sm"
                                                                        onClick={() => moveAsset(index, 'down')}
                                                                        disabled={index === formData.assets.length - 1}
                                                                        title="Move Down"
                                                                    >
                                                                        <MoveDown size={14} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => removeAssetFromList(index)}
                                                                        title="Remove"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="text-center">
                                                                {asset.asset ? (
                                                                    asset.assetType === 'image' ? (
                                                                        <div className="bg-light rounded p-2 border position-relative">
                                                                            <img 
                                                                                src={assetPath ? `${assetPath}/${asset.asset}` : asset.asset} 
                                                                                alt="Asset Preview" 
                                                                                className="img-fluid rounded shadow-sm"
                                                                                style={{ 
                                                                                    maxHeight: '80px', 
                                                                                    maxWidth: '100%', 
                                                                                    objectFit: 'cover',
                                                                                    cursor: 'pointer',
                                                                                    transition: 'transform 0.2s ease-in-out'
                                                                                }}
                                                                                onClick={() => previewAssetFile(asset)}
                                                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.nextSibling.style.display = 'block';
                                                                                }}
                                                                            />
                                                                            <div style={{ display: 'none' }} className="mt-1">
                                                                                <small className="text-muted d-block fw-bold">{asset.assetType.toUpperCase()}</small>
                                                                                <small className="text-muted">{asset.asset}</small>
                                                                            </div>
                                                                            <div className="position-absolute top-0 end-0 m-1">
                                                                                <small className="badge bg-primary">Click to preview</small>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="bg-light rounded p-2 border">
                                                                            <Video size={32} className="text-danger mx-auto d-block mb-1" />
                                                                            <small className="text-muted d-block fw-bold">{asset.assetType.toUpperCase()}</small>
                                                                            <small className="text-muted">{asset.asset}</small>
                                                                        </div>
                                                                    )
                                                                ) : (
                                                                    <div className="bg-light rounded p-2 border">
                                                                        <small className="text-danger">No file</small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <small className="text-muted">
                                        Assets will be displayed in order of priority. Use the up/down buttons to reorder, then click Update to save changes.
                                    </small>
                                </div>
                            </div>

                            {/* Benefits Section */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Star size={20} className="me-2 text-warning" />
                                        Benefits ({formData.benefits.length})
                                    </h5>
                                    <small className="text-muted mt-1">
                                        💡 Tip: Add benefits first, then edit them to upload icons
                                    </small>
                                </div>
                                <div className="card-body">
                                    {/* Add New Benefit */}
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <h6 className="mb-3">Add Benefit</h6>
                                        <div className="row">
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label small fw-bold">Benefit Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Enter benefit title"
                                                    value={newBenefit.title}
                                                    onChange={(e) => setNewBenefit(prev => ({
                                                        ...prev,
                                                        title: e.target.value
                                                    }))}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-2">
                                                <label className="form-label small fw-bold">Icon (optional)</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Icon will be available after saving"
                                                    disabled
                                                />
                                                <small className="text-muted d-block mt-1">
                                                    Save the benefit first, then edit to add an icon
                                                </small>
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label small fw-bold">Description</label>
                                            <textarea
                                                className="form-control form-control-sm"
                                                placeholder="Enter benefit description"
                                                value={newBenefit.description}
                                                onChange={(e) => setNewBenefit(prev => ({
                                                    ...prev,
                                                    description: e.target.value
                                                }))}
                                                rows="2"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={addBenefit}
                                            disabled={uploadingBenefitIcon}
                                        >
                                            {uploadingBenefitIcon ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <Plus size={14} className="me-1" />
                                                    Add Benefit
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Benefits List */}
                                    {formData.benefits.map((benefit, index) => (
                                        <div key={index} className="border rounded p-3 mb-2">
                                            {editingBenefitIndex === index ? (
                                                // Editing Mode
                                                <div>
                                                    <div className="row">
                                                        <div className="col-md-6 mb-2">
                                                            <label className="form-label small fw-bold">Benefit Title</label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Enter benefit title"
                                                                value={editingBenefit.title}
                                                                onChange={(e) => setEditingBenefit(prev => ({
                                                                    ...prev,
                                                                    title: e.target.value
                                                                }))}
                                                            />
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <label className="form-label small fw-bold">Icon (optional)</label>
                                                            <input
                                                                type="file"
                                                                className="form-control form-control-sm"
                                                                id={`editing-benefit-icon-${index}`}
                                                                accept="image/*"
                                                                onChange={handleEditingBenefitIconChange}
                                                            />
                                                            {editingBenefitIconFile && (
                                                                <small className="text-muted d-block mt-1">
                                                                    New icon: {editingBenefitIconFile.name}
                                                                </small>
                                                            )}
                                                            {editingBenefit.icon && !editingBenefitIconFile && (
                                                                <small className="text-muted d-block mt-1">
                                                                    Current: {editingBenefit.icon}
                                                                </small>
                                                            )}
                                                            <small className="text-muted d-block mt-1">
                                                                💡 You can now upload an icon for this benefit
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <textarea
                                                            className="form-control form-control-sm"
                                                            placeholder="Benefit Description"
                                                            value={editingBenefit.description}
                                                            onChange={(e) => setEditingBenefit(prev => ({
                                                                ...prev,
                                                                description: e.target.value
                                                            }))}
                                                            rows="2"
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={saveEditingBenefit}
                                                            disabled={uploadingBenefitIcon}
                                                        >
                                                            {uploadingBenefitIcon ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Save size={14} className="me-1" />
                                                                    Save
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={cancelEditingBenefit}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Display Mode
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-start mb-2">
                                                            {benefit.icon && (
                                                                <img 
                                                                    src={assetPath ? `${assetPath}/${benefit.icon}` : benefit.icon} 
                                                                    alt="Benefit Icon" 
                                                                    className="me-3 rounded shadow-sm"
                                                                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <h6 className="mb-1 fw-bold">{benefit.title}</h6>
                                                                <p className="text-muted small mb-0">{benefit.description}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="btn-group">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => startEditingBenefit(index)}
                                                            title="Edit"
                                                        >
                                                            <Edit3 size={14} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => removeBenefit(index)}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* FAQs Section */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <HelpCircle size={20} className="me-2 text-info" />
                                        FAQs ({formData.faqs.length})
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {/* Add New FAQ */}
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <h6 className="mb-3">Add FAQ</h6>
                                        <div className="mb-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm mb-2"
                                                placeholder="FAQ Title/Question"
                                                value={newFaq.title}
                                                onChange={(e) => setNewFaq(prev => ({
                                                    ...prev,
                                                    title: e.target.value
                                                }))}
                                            />
                                            <textarea
                                                className="form-control form-control-sm"
                                                placeholder="FAQ Answer/Description"
                                                value={newFaq.description}
                                                onChange={(e) => setNewFaq(prev => ({
                                                    ...prev,
                                                    description: e.target.value
                                                }))}
                                                rows="2"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            onClick={addFaq}
                                        >
                                            <Plus size={14} className="me-1" />
                                            Add FAQ
                                        </button>
                                    </div>

                                    {/* FAQs List */}
                                    {formData.faqs.map((faq, index) => (
                                        <div key={index} className="border rounded p-3 mb-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1">{faq.title}</h6>
                                                    <p className="text-muted small mb-0">{faq.description}</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => removeFaq(index)}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm sticky-top" style={{top: '1rem'}}>
                                <div className="card-header bg-white border-bottom">
                                    <h5 className="mb-0">Actions</h5>
                                </div>
                                <div className="card-body">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 mb-3 d-flex align-items-center justify-content-center"
                                        disabled={loading}
                                    >
                                        <Save size={16} className="me-2" />
                                        {loading ? 'Saving...' : (isNew ? 'Create Referral Content' : 'Update Referral Content')}
                                    </button>

                                    <Link
                                        to="/inventory/v1.2/referral-contents"
                                        className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
                                    >
                                        <ArrowLeft size={16} className="me-2" />
                                        Cancel
                                    </Link>
                                </div>

                                {/* Content Summary */}
                                <div className="card-footer bg-light">
                                    <h6 className="mb-3">Referral Content Summary</h6>
                                    <div className="row g-2 text-center">
                                        <div className="col-6">
                                            <div className="bg-white rounded p-2">
                                                <div className="h6 mb-0 text-primary">{formData.assets.length}</div>
                                                <small className="text-muted">Assets</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="bg-white rounded p-2">
                                                <div className="h6 mb-0 text-warning">{formData.benefits.length}</div>
                                                <small className="text-muted">Benefits</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="bg-white rounded p-2">
                                                <div className="h6 mb-0 text-info">{formData.faqs.length}</div>
                                                <small className="text-muted">FAQs</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="bg-white rounded p-2">
                                                <div className="h6 mb-0 text-success">
                                                    {formData.active ? 'Active' : 'Inactive'}
                                                </div>
                                                <small className="text-muted">Status</small>
                                            </div>
                                        </div>
                                    </div>
                                    {formData.message && (
                                        <div className="mt-3">
                                            <h6 className="mb-2">Message Preview</h6>
                                            <div className="bg-white rounded p-2 text-start">
                                                <small className="text-muted">
                                                    {formData.message.length > 100 
                                                        ? `${formData.message.substring(0, 100)}...` 
                                                        : formData.message
                                                    }
                                                </small>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {/* File Upload Section - Only shown when editing an existing item */}
                {!isNew && (
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0 d-flex align-items-center">
                                <Upload size={18} className="me-2 text-primary" />
                                Upload Assets
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Asset Type</label>
                                    <select
                                        className="form-select"
                                        value={fileType}
                                        onChange={handleFileTypeChange}
                                    >
                                        <option value="image">Image</option>
                                        <option value="video">Video</option>
                                    </select>
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label">Select File</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={handleFileChange}
                                        accept={fileType === 'image' ? 'image/*' : 'video/*'}
                                        key={`upload-${fileType}`}
                                        id={`file-upload-${fileType}`}
                                    />
                                </div>
                            </div>
                            
                            {uploadFile && (
                                <div className="alert alert-info mt-3 d-flex align-items-center">
                                    <div className="me-3">
                                        {fileType === 'image' ? (
                                            <ImageIcon size={24} className="text-primary" />
                                        ) : (
                                            <Video size={24} className="text-danger" />
                                        )}
                                    </div>
                                    <div className="flex-grow-1">
                                        <p className="mb-0"><strong>Selected File:</strong> {uploadFile.name}</p>
                                        <small>Size: {Math.round(uploadFile.size / 1024)} KB | Type: {uploadFile.type}</small>
                                    </div>
                                </div>
                            )}
                            
                            <div className="d-flex justify-content-end mt-3">
                                <button
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={uploadAsset}
                                    disabled={!uploadFile || uploadingAsset}
                                >
                                    {uploadingAsset ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={16} className="me-2" />
                                            Upload Asset
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Asset Preview Modal */}
            {previewAsset && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title d-flex align-items-center">
                                    {previewAsset.assetType === 'image' ? (
                                        <ImageIcon size={20} className="me-2 text-primary" />
                                    ) : (
                                        <Video size={20} className="me-2 text-danger" />
                                    )}
                                    Asset Preview
                                </h5>
                                <button type="button" className="btn-close" onClick={closePreview}></button>
                            </div>
                            <div className="modal-body text-center">
                                {previewAsset.assetType === 'image' ? (
                                    <img 
                                        src={assetPath ? `${assetPath}/${previewAsset.asset}` : previewAsset.asset} 
                                        alt="Asset Preview" 
                                        className="img-fluid" 
                                        style={{ maxHeight: '60vh' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                    />
                                ) : (
                                    <video 
                                        src={assetPath ? `${assetPath}/${previewAsset.asset}` : previewAsset.asset} 
                                        controls 
                                        className="w-100" 
                                        style={{ maxHeight: '60vh' }}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                )}
                                <div style={{ display: 'none' }} className="alert alert-warning">
                                    <p>Cannot preview this asset. The file may not be accessible or may not be a valid image/video.</p>
                                </div>
                                <div className="mt-3">
                                    <small className="text-muted d-block">File: {previewAsset.asset}</small>
                                    <small className="text-muted d-block">Type: {previewAsset.assetType}</small>
                                    <small className="text-muted d-block">Priority: {previewAsset.priority}</small>
                                    {assetPath && (
                                        <small className="text-muted d-block">Full Path: {`${assetPath}/${previewAsset.asset}`}</small>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closePreview}>
                                    Close
                                </button>
                                <a 
                                    href={assetPath ? `${assetPath}/${previewAsset.asset}` : previewAsset.asset} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn btn-primary"
                                >
                                    Open in New Tab
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

ReferralContent.propTypes = {
    referralContent: PropTypes.object.isRequired,
    getReferralContent: PropTypes.func.isRequired,
    addReferralContent: PropTypes.func.isRequired,
    updateReferralContent: PropTypes.func.isRequired,
    clearReferralContent: PropTypes.func.isRequired,
    uploadReferralContentAsset: PropTypes.func.isRequired,
    uploadBenefitIcon: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
    referralContent: state.referralContent
});

export default connect(mapStateToProps, { 
    getReferralContent, 
    addReferralContent, 
    updateReferralContent, 
    clearReferralContent,
    uploadReferralContentAsset,
    uploadBenefitIcon
})(ReferralContent); 